/**
 * Tiny CRM API client. Uses fetch with `credentials: 'include'` so the
 * existing HttpOnly admin cookie is sent on every call. Returns JSON or
 * throws an Error with the server-provided detail.
 *
 * IMPORTANT: We resolve the API base at runtime using `window.location.origin`
 * whenever we're in a browser. This guarantees the API is called on the same
 * origin as the page (e.g., blackrdg.com → blackrdg.com/api/crm), which keeps
 * the SameSite=Lax HttpOnly cookie first-party and lets the browser send it.
 * Build-time REACT_APP_BACKEND_URL is only used as a non-browser fallback.
 */
function resolveCrmBase() {
  if (typeof window !== "undefined" && window.location && window.location.origin) {
    return `${window.location.origin}/api/crm`;
  }
  const configured = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");
  return `${configured}/api/crm`;
}

const BASE = resolveCrmBase();

async function handle(res) {
  if (res.ok) {
    if (res.status === 204) return null;
    const ct = res.headers.get("content-type") || "";
    return ct.includes("json") ? res.json() : res.text();
  }
  let detail = `HTTP ${res.status}`;
  try {
    const j = await res.json();
    detail = j.detail || j.message || detail;
  } catch (_) {
    /* ignore */
  }
  const err = new Error(detail);
  err.status = res.status;
  throw err;
}

function url(path, params) {
  let u = `${BASE}${path}`;
  if (params && Object.keys(params).length) {
    const qs = new URLSearchParams(
      Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ""),
    ).toString();
    if (qs) u += `?${qs}`;
  }
  return u;
}

export async function getJson(path, params) {
  const res = await fetch(url(path, params), { credentials: "include" });
  return handle(res);
}

export async function postJson(path, body) {
  const res = await fetch(url(path), {
    method: "POST",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  return handle(res);
}

export async function patchJson(path, body) {
  const res = await fetch(url(path), {
    method: "PATCH",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  return handle(res);
}

export async function putJson(path, body) {
  const res = await fetch(url(path), {
    method: "PUT",
    credentials: "include",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify(body || {}),
  });
  return handle(res);
}

export async function deleteJson(path) {
  const res = await fetch(url(path), {
    method: "DELETE",
    credentials: "include",
  });
  return handle(res);
}

export async function postForm(path, formData) {
  const res = await fetch(url(path), {
    method: "POST",
    credentials: "include",
    body: formData,
  });
  return handle(res);
}

// Convenience: route shorthands used by the Owner UI.
export const crmApi = {
  me: () => getJson("/me"),

  // Handoff bundle (owner-only)
  listHandoffFiles: () => getJson("/handoff/files"),
  handoffDownloadUrl: (filename) =>
    url(`/handoff/download/${encodeURIComponent(filename)}`),
  // Library
  listBinders: (includeVault = false) => getJson("/library/binders", { include_owner_vault: includeVault }),
  createBinder: (payload) => postJson("/library/binders", payload),
  listVersions: (binderId) => getJson(`/library/binders/${binderId}/versions`),
  uploadVersion: (binderId, fd) => postForm(`/library/binders/${binderId}/versions`, fd),
  activateVersion: (binderId, versionId) =>
    postJson(`/library/binders/${binderId}/activate`, { binder_version_id: versionId }),
  diffVersions: (fromId, toId) => getJson("/library/diff", { from_version_id: fromId, to_version_id: toId }),
  listFacts: (versionId) => getJson(`/library/versions/${versionId}/facts`),
  categories: () => getJson("/library/categories"),
  // Vault
  listVault: (kind) => getJson("/vault/entries", { kind }),
  upsertVault: (payload) => postJson("/vault/entries", payload),
  deleteVault: (key) => deleteJson(`/vault/entries/${encodeURIComponent(key)}`),
  // Audit
  audit: (params) => getJson("/audit", params),
  // Agents
  listAgents: () => getJson("/agents"),
  getAgent: (slug) => getJson(`/agents/${slug}`),
  chatAgent: (slug, payload) => postJson(`/agents/${slug}/chat`, payload),
  agentHistory: (slug, limit = 10) => getJson(`/agents/${slug}/history`, { limit }),
  // Stripe
  stripeStatus: () => getJson("/stripe/status"),
  listTiers: () => getJson("/stripe/tiers"),
  upsertTier: (payload) => postJson("/stripe/tiers", payload),
  provisionTier: (slug) => postJson(`/stripe/tiers/${slug}/provision`),
  // Pipeline
  pipelineHealth: () => getJson("/pipeline-health"),
  // Phase E — Projects + phases + takeoffs + change orders + notes + HD + weather
  listProjects: (status) => getJson("/projects", { status }),
  createProject: (payload) => postJson("/projects", payload),
  getProject: (id) => getJson(`/projects/${id}`),
  updateProject: (id, payload) => patchJson(`/projects/${id}`, payload),
  deleteProject: (id) => deleteJson(`/projects/${id}`),
  listPhases: (id) => getJson(`/projects/${id}/phases`),
  createPhase: (id, payload) => postJson(`/projects/${id}/phases`, payload),
  updatePhase: (phaseId, payload) => patchJson(`/projects/phases/${phaseId}`, payload),
  deletePhase: (phaseId) => deleteJson(`/projects/phases/${phaseId}`),
  listTakeoffs: (id) => getJson(`/projects/${id}/takeoffs`),
  createTakeoff: (id, payload) => postJson(`/projects/${id}/takeoffs`, payload),
  getTakeoff: (toId) => getJson(`/projects/takeoffs/${toId}`),
  updateTakeoff: (toId, payload) => patchJson(`/projects/takeoffs/${toId}`, payload),
  deleteTakeoff: (toId) => deleteJson(`/projects/takeoffs/${toId}`),
  listChangeOrders: (id) => getJson(`/projects/${id}/change-orders`),
  createChangeOrder: (id, payload) => postJson(`/projects/${id}/change-orders`, payload),
  updateChangeOrder: (coId, payload) => patchJson(`/projects/change-orders/${coId}`, payload),
  listNotes: (id) => getJson(`/projects/${id}/notes`),
  addNote: (id, payload) => postJson(`/projects/${id}/notes`, payload),
  hdStatus: () => getJson("/home-depot/status"),
  projectWeather: (id, days = 5) => getJson(`/projects/${id}/weather`, { days }),
  weather: (zipCode, days = 5) => getJson("/weather", { zip_code: zipCode, days }),
  // Phase F — Analytics
  analyticsSnapshots: (days = 30) => getJson("/analytics/snapshots", { days }),
  analyticsUpsertToday: () => postJson("/analytics/snapshot/today"),
  analyticsDetect: () => postJson("/analytics/anomalies/detect"),
  analyticsAnomalies: (params = {}) => getJson("/analytics/anomalies", params),
  analyticsAck: (id) => postJson(`/analytics/anomalies/${id}/ack`),
  // Phase G — Tenant config
  getTenantConfig: () => getJson("/tenant-config"),
  updateTenantConfig: (payload) => patchJson("/tenant-config", payload),
  listTenantConfigs: () => getJson("/tenant-configs"),
  // Phase C — Plans + Members + Dispatches + Attach
  plansSummary: () => getJson("/plans/summary"),
  createCheckout: (payload) => postJson("/plans/checkout", payload),
  listMembers: (params = {}) => getJson("/members", params),
  getMember: (id) => getJson(`/members/${id}`),
  createMember: (payload) => postJson("/members", payload),
  updateMember: (id, payload) => patchJson(`/members/${id}`, payload),
  issueMagicLink: (id) => postJson(`/members/${id}/magic-link`),
  regenerateDispatches: (id, visitCount = 6) => postJson(`/members/${id}/dispatches/generate?visit_count=${visitCount}`),
  listDispatches: (params = {}) => getJson("/dispatches", params),
  completeDispatch: (id, params = {}) => {
    const qs = new URLSearchParams(Object.entries(params).filter(([, v]) => v)).toString();
    return postJson(`/dispatches/${id}/complete${qs ? `?${qs}` : ""}`);
  },
  skipDispatch: (id, reason) => postJson(`/dispatches/${id}/skip?reason=${encodeURIComponent(reason)}`),
  listAttach: (outcome) => getJson("/attach/events", outcome ? { outcome } : {}),
  createAttach: (payload) => postJson("/attach/events", payload),
  updateAttach: (id, payload) => patchJson(`/attach/events/${id}`, payload),
  // Phase D — Outbound + Nurture Engine
  outboundSummary: () => getJson("/outbound/summary"),
  listTemplates: (params = {}) => getJson("/templates", params),
  getTemplate: (slug) => getJson(`/templates/${slug}`),
  upsertTemplate: (slug, payload) => putJson(`/templates/${slug}`, payload),
  previewTemplate: (slug, context) => postJson(`/templates/${slug}/preview`, context || {}),
  listSequences: (params = {}) => getJson("/nurture/sequences", params),
  getSequence: (slug) => getJson(`/nurture/sequences/${slug}`),
  upsertSequence: (slug, payload) => putJson(`/nurture/sequences/${slug}`, payload),
  listEnrollments: (params = {}) => getJson("/nurture/enrollments", params),
  createEnrollment: (payload) => postJson("/nurture/enrollments", payload),
  updateEnrollment: (id, payload) => patchJson(`/nurture/enrollments/${id}`, payload),
  triggerNurtureTick: () => postJson("/nurture/tick"),
  seedNurtureDefaults: () => postJson("/nurture/seed-defaults"),
  listEmailMessages: (params = {}) => getJson("/email/messages", params),
  getEmailMessage: (id) => getJson(`/email/messages/${id}`),
  sendTestEmail: (payload) => postJson("/email/test", payload),
  // Phase B-Lite — Leads
  listLeads: (params = {}) => getJson("/leads", params),
  leadsSummary: () => getJson("/leads/summary"),
  getLead: (id) => getJson(`/leads/${id}`),
  updateLead: (id, payload) => patchJson(`/leads/${id}`, payload),
  // Phase E2 — Cold Outreach Prospects
  prospectsProbe: () => getJson("/prospects/probe"),
  prospectsSummary: () => getJson("/prospects/summary"),
  listProspects: (params = {}) => getJson("/prospects", params),
  getProspect: (id) => getJson(`/prospects/${id}`),
  updateProspect: (id, payload) => patchJson(`/prospects/${id}`, payload),
  runProspectResearch: (payload) => postJson("/prospects/research", payload),
  promoteProspect: (id, payload) => postJson(`/prospects/${id}/promote`, payload || {}),
  suppressProspect: (id, payload) => postJson(`/prospects/${id}/suppress`, payload || { reason: "manual" }),
  // Phase E2.2 — Compliance (Dana)
  complianceSettings: () => getJson("/compliance/settings"),
  danaCheck: (payload) => postJson("/compliance/dana/check", payload),
  suppressionsSummary: () => getJson("/compliance/suppressions/summary"),
  listSuppressions: (params = {}) => getJson("/compliance/suppressions", params),
  addSuppression: (payload) => postJson("/compliance/suppressions", payload),
  removeSuppression: (email) => deleteJson(`/compliance/suppressions/${encodeURIComponent(email)}`),
  // Phase A.2 — Agent Knowledge Requests
  listKnowledgeRequests: (slug, params = {}) =>
    getJson(`/agents/${slug}/knowledge-requests`, params),
  knowledgeRequestsSummary: () => getJson("/agents/knowledge-requests/summary"),
  createKnowledgeRequest: (slug, payload) =>
    postJson(`/agents/${slug}/knowledge-requests`, payload),
  answerKnowledgeRequest: (slug, id, payload) =>
    patchJson(`/agents/${slug}/knowledge-requests/${id}`, payload),
  dismissKnowledgeRequest: (slug, id) =>
    deleteJson(`/agents/${slug}/knowledge-requests/${id}`),

  // Phase E.3 — 24/7 Autonomous Prospector
  prospectorSettings: () => getJson("/prospector/settings"),
  updateProspectorSettings: (payload) => patchJson("/prospector/settings", payload),
  prospectorStatus: () => getJson("/prospector/status"),
  prospectorTicks: (params = {}) => getJson("/prospector/ticks", params),
  forceProspectorTick: () => postJson("/prospector/tick", {}),

  // Phase E.4 — Bulk ops + Campaign Metrics + Activity feed
  bulkPromoteProspects: (payload) => postJson("/prospects/bulk-promote", payload),
  bulkSuppressProspects: (payload) => postJson("/prospects/bulk-suppress", payload),
  campaignMetrics: (params = {}) => getJson("/metrics/campaign", params),
  emailMetrics: (params = {}) => getJson("/metrics/email", params),
  funnelMetrics: (params = {}) => getJson("/metrics/funnel", params),
  dailyMetrics: (params = {}) => getJson("/metrics/daily", params),
  activityFeed: (params = {}) => getJson("/activity/feed", params),

  // Phase F.1 — Maintenance Plan Waitlist
  listWaitlist: (params = {}) => getJson("/waitlist", params),
  waitlistSummary: () => getJson("/waitlist/summary"),
  waitlistOptions: () => getJson("/waitlist/options"),
  getWaitlistEntry: (id) => getJson(`/waitlist/${encodeURIComponent(id)}`),
  updateWaitlistEntry: (id, payload) => patchJson(`/waitlist/${encodeURIComponent(id)}`, payload),
  contactWaitlistEntry: (id, payload) => postJson(`/waitlist/${encodeURIComponent(id)}/contact`, payload),
  convertWaitlistEntry: (id, payload) => postJson(`/waitlist/${encodeURIComponent(id)}/convert`, payload),
  deleteWaitlistEntry: (id) => deleteJson(`/waitlist/${encodeURIComponent(id)}`),
  waitlistCsvUrl: () => `${BASE}/waitlist/export.csv`,

  // Phase G.1 — Swarm console
  swarmStatus: () => getJson("/swarm/status"),
  swarmSettings: (payload) => patchJson("/swarm/settings", payload),
  swarmRun: (payload = {}) => postJson("/swarm/run", payload),
  swarmCycles: (params = {}) => getJson("/swarm/cycles", params),
  swarmContent: (params = {}) => getJson("/swarm/content", params),
  swarmUpdateContent: (id, payload) => patchJson(`/swarm/content/${encodeURIComponent(id)}`, payload),
  swarmAudiences: (params = {}) => getJson("/swarm/audiences", params),
  swarmUpdateAudience: (id, payload) => patchJson(`/swarm/audiences/${encodeURIComponent(id)}`, payload),
  swarmLeadDrafts: (params = {}) => getJson("/swarm/lead-drafts", params),
  swarmUpdateLeadDraft: (id, payload) => patchJson(`/swarm/lead-drafts/${encodeURIComponent(id)}`, payload),
  swarmEstimates: (params = {}) => getJson("/swarm/estimates", params),
  swarmDailySummary: () => getJson("/swarm/daily-summary"),

  // Phase G.3 — Native Calendar (owner / ambassador / swarm-only scheduling)
  calendarAvailability: () => getJson("/calendar/availability"),
  calendarUpdateAvailability: (payload) => patchJson("/calendar/availability", payload),
  calendarSlots: (params = {}) => getJson("/calendar/slots", params),
  calendarToday: () => getJson("/calendar/today"),
  calendarAppointments: (params = {}) => getJson("/calendar/appointments", params),
  calendarCreateAppointment: (payload) => postJson("/calendar/appointments", payload),
  calendarGetAppointment: (id) => getJson(`/calendar/appointments/${encodeURIComponent(id)}`),
  calendarUpdateAppointment: (id, payload) => patchJson(`/calendar/appointments/${encodeURIComponent(id)}`, payload),
  calendarCancelAppointment: (id, payload) => postJson(`/calendar/appointments/${encodeURIComponent(id)}/cancel`, payload),
  calendarIcsUrl: (id) => `${BASE}/calendar/appointments/${encodeURIComponent(id)}/ics`,
};
